import { TextField } from '@mui/material';
import ErrorText from 'components/ErrorText/ErrorText';
import {
  Control,
  Controller,
  FieldErrors,
  FieldPath,
  FieldValues,
  PathValue,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';

interface FormFieldProps<TFormValues extends FieldValues> {
  name: FieldPath<TFormValues>;
  control: Control<TFormValues>;
  errors: FieldErrors<TFormValues>;
  label: string;
  placeholder?: string;
  rules?: object;
  onChange?: (value: string) => void;
  fullWidth?: boolean;
  type?: 'text' | 'email' | 'password' | 'number' | 'date' | 'url';
  multiline?: boolean;
  rows?: number;
}

export default function FormField<TFormValues extends FieldValues>({
  name,
  control,
  errors,
  label,
  placeholder,
  rules = { required: true },
  onChange,
  fullWidth = true,
  type = 'text',
  multiline = false,
  rows,
}: FormFieldProps<TFormValues>) {
  const { t } = useTranslation();
  const error = errors[name];

  return (
    <>
      <Controller
        name={name}
        control={control}
        defaultValue={'' as PathValue<TFormValues, FieldPath<TFormValues>>}
        rules={rules}
        render={({ field }) => (
          <TextField
            {...field}
            type={type}
            label={label}
            placeholder={type === 'date' ? '' : placeholder}
            fullWidth={fullWidth}
            multiline={multiline}
            rows={multiline ? rows : undefined}
            error={!!error}
            helperText={error && t('formErrors.nameRequired')}
            InputLabelProps={type === 'date' ? { shrink: true } : undefined}
            inputProps={type === 'date' ? { placeholder: '' } : undefined}
            onChange={(e) => {
              field.onChange(e);
              if (onChange) {
                onChange(e.target.value);
              }
            }}
          />
        )}
      />
      {error && <ErrorText>{t('formErrors.nameRequired')}</ErrorText>}
    </>
  );
}
